import {
  AcademicGrade,
  AcademicTerm,
  AppIcon,
  TrackAssessmentResult,
  TIERS,
  Tier,
  TIER_TO_CAREER_TRACK_NAME,
  TRACK_CODE_TO_NAME,
  COLOR_TOKENS,
  UnlockStatus,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Avatar, Box, SxProps, Theme, Tooltip, Typography } from '@mui/material';

import { getStatusColor } from '../trackAssessment.constants';

const TIMELINE_CHART_STRINGS = appStrings.modules.assessMyTrack.timelineChart;

const GRADE_DISPLAY_LABELS: Record<string, string> = {
  '9': TIMELINE_CHART_STRINGS.gradeLabels.grade9,
  '10': TIMELINE_CHART_STRINGS.gradeLabels.grade10,
  '11': TIMELINE_CHART_STRINGS.gradeLabels.grade11,
  '12': TIMELINE_CHART_STRINGS.gradeLabels.grade12,
  'Year 1 Post Secondary': TIMELINE_CHART_STRINGS.gradeLabels.postSecondaryYear1,
  'Year 2 Post Secondary': TIMELINE_CHART_STRINGS.gradeLabels.postSecondaryYear2,
  'Year 3 Post Secondary': TIMELINE_CHART_STRINGS.gradeLabels.postSecondaryYear3,
  'Year 4 Post Secondary': TIMELINE_CHART_STRINGS.gradeLabels.postSecondaryYear4,
};

export type AssessmentDataPoint = Readonly<{
  gradeValue: string;
  term: AcademicTerm;
  result: TrackAssessmentResult;
}>;

const TERM_OFFSETS: Record<AcademicTerm, number> = {
  Fall: 0.0,
  Winter: 1 / 4,
  Spring: 2 / 4,
  Summer: 3 / 4,
};

/** e.g. "9th Grade - Fall" or "Y2 Post - Spring" */
const getDataPointLabel = (gradeValue: string, term: AcademicTerm): string => {
  const gradeLabel = GRADE_DISPLAY_LABELS[gradeValue] ?? gradeValue;

  return `${gradeLabel} - ${term}`;
};

// extrapolation slope constants (in units of "Tracks per Year")
// 1 track per 2 years = 0.5 tracks/year
const EXTRAPOLATION_POSITIVE_SLOPE = 0.5;
const EXTRAPOLATION_NEGATIVE_SLOPE = 0.5;

type TimelineChartSvgProps = Readonly<{
  academicGrades: readonly AcademicGrade[];
  assessmentDataPoints: readonly AssessmentDataPoint[];
  minimumTrack: Tier | undefined;
  profileImageUrl?: string;
}>;

const TRACK_ORDER = [TIERS.TOP, TIERS.UPPER, TIERS.MID, TIERS.TRADE, TIERS.SEMISKILLED] as const;

const getTierFromResult = (result: TrackAssessmentResult): Tier =>
  result.careerTrack ? TRACK_CODE_TO_NAME[result.careerTrack.code] : TIERS.MID;

const CHART_WIDTH = 800;
const PADDING_LEFT = 120;
const PADDING_RIGHT = 20;
const PADDING_TOP = 29;
const LABEL_AREA_TOP = 38;
const LABEL_AREA_HEIGHT = 56;
const CHART_HEIGHT = PADDING_TOP + 232 + LABEL_AREA_TOP + LABEL_AREA_HEIGHT;
const PADDING_BOTTOM = LABEL_AREA_TOP + LABEL_AREA_HEIGHT;
const PLOT_HEIGHT = CHART_HEIGHT - PADDING_TOP - PADDING_BOTTOM;
const PLOT_WIDTH = CHART_WIDTH - PADDING_LEFT - PADDING_RIGHT;

const getTierYIndex = (tier: Tier): number =>
  TRACK_ORDER.indexOf(tier as (typeof TRACK_ORDER)[number]);

const yScale = (tierIndex: number) =>
  PADDING_TOP + (tierIndex / (TRACK_ORDER.length - 1)) * PLOT_HEIGHT;

const PROFILE_OUTER_SIZE = 48;
const X_INSET = PROFILE_OUTER_SIZE / 2;

/** Number of visual-only columns after the real academic grades ("Early Career") */
const EXTRA_LABEL_COLUMNS = 1;

/** X position for a display column within the total column layout */
const getColumnXPosition = (columnIndex: number, totalColumns: number) => {
  if (totalColumns <= 1) return PADDING_LEFT + PLOT_WIDTH / 2;

  const usableWidth = PLOT_WIDTH - 2 * X_INSET;
  const spacing = usableWidth / (totalColumns - 1);

  return PADDING_LEFT + X_INSET + columnIndex * spacing;
};

/** X position for a grade column (grades occupy indices 0..gradeCount-1) */
const getGradeXPosition = (gradeIndex: number, gradeCount: number) =>
  getColumnXPosition(gradeIndex, gradeCount + EXTRA_LABEL_COLUMNS);

/** X position for a data point, offset within its grade column by academic term */
const getDataPointX = (
  gradeValue: string,
  term: AcademicTerm,
  academicGrades: readonly AcademicGrade[],
) => {
  const gradeCount = academicGrades.length;
  const gradeIndex = Math.max(
    0,
    academicGrades.findIndex((g) => g.value === gradeValue),
  );
  const baseX = getGradeXPosition(gradeIndex, gradeCount);

  if (gradeCount <= 1) return baseX;

  const totalColumns = gradeCount + EXTRA_LABEL_COLUMNS;
  const columnSpacing = (PLOT_WIDTH - 2 * X_INSET) / (totalColumns - 1);

  return baseX + TERM_OFFSETS[term] * columnSpacing;
};

const MIN_RANGE_SPAN = 1; // minimum tier-index gap between upper and lower range lines

const getRangeIndices = (originalTierIndex: number) => {
  const maxIndex = TRACK_ORDER.length - 1;

  const upperIndex = originalTierIndex;
  let lowerIndex = originalTierIndex + 2;

  lowerIndex = Math.min(lowerIndex, maxIndex);

  const clampedUpperIndex =
    lowerIndex - upperIndex < MIN_RANGE_SPAN
      ? Math.max(0, lowerIndex - MIN_RANGE_SPAN)
      : upperIndex;

  const midIndex = (clampedUpperIndex + lowerIndex) / 2;

  return { upperIndex: clampedUpperIndex, midIndex, lowerIndex };
};

const SMALL_CIRCLE_RADIUS = 7;

// Pre-revamp assessments have no profile strength; derive a fallback color so the dot isn't gray.
const getFallbackStatus = (
  result: TrackAssessmentResult,
  minimumTrack: Tier | undefined,
  currentStatus: UnlockStatus | null,
): UnlockStatus => {
  if (currentStatus === 'not_yet') return 'not_yet';
  if (!minimumTrack) return 'developing';

  return getTierYIndex(getTierFromResult(result)) <= getTierYIndex(minimumTrack)
    ? 'developing'
    : 'not_yet';
};

const getEffectiveStatus = (
  result: TrackAssessmentResult,
  minimumTrack: Tier | undefined,
  currentStatus: UnlockStatus | null,
): UnlockStatus | null =>
  result.profileStrength?.overallUnlockStatus ??
  getFallbackStatus(result, minimumTrack, currentStatus);

export const TimelineChartSvg = ({
  academicGrades,
  assessmentDataPoints,
  minimumTrack,
  profileImageUrl,
}: TimelineChartSvgProps) => {
  const gradeCount = academicGrades.length;

  const currentStatus: UnlockStatus | null =
    assessmentDataPoints.at(-1)?.result.profileStrength?.overallUnlockStatus ?? null;

  return (
    <Box sx={chartContainerSx}>
      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Blueprint target track shading */}
        {minimumTrack &&
          (() => {
            const bottomY = yScale(getTierYIndex(minimumTrack) + 0.5);
            const topY = 0;

            return (
              <rect
                x={PADDING_LEFT}
                y={topY}
                width={CHART_WIDTH - PADDING_LEFT}
                height={bottomY - topY}
                fill={COLOR_TOKENS['Blue/100']}
                fillOpacity={0.5}
              />
            );
          })()}

        {/* Grid lines at tier boundaries (shifted down half a step) */}
        {Array.from({ length: TRACK_ORDER.length + 1 }, (_, i) => i - 0.5)
          .filter((boundaryIndex) => boundaryIndex > -0.5)
          .map((boundaryIndex) => {
            const y = yScale(boundaryIndex);

            return (
              <line
                key={`grid-${boundaryIndex}`}
                x1={PADDING_LEFT}
                y1={y}
                x2={CHART_WIDTH}
                y2={y}
                stroke={COLOR_TOKENS['Colors/Border/border-secondary']}
                strokeWidth={1}
              />
            );
          })}

        {/* Y-axis tier labels (centered between grid lines) */}
        {TRACK_ORDER.map((tier, index) => {
          const y = yScale(index);

          return (
            <foreignObject key={tier} x={0} y={y - 10} width={PADDING_LEFT - 12} height={20}>
              <Typography variant="text-sm-regular" sx={yAxisLabelSx}>
                {TIER_TO_CAREER_TRACK_NAME[tier]}
              </Typography>
            </foreignObject>
          );
        })}

        {/* Range shaded area, gray dotted range lines, and connecting line */}
        {assessmentDataPoints.length > 0 &&
          (() => {
            const rangePoints = assessmentDataPoints.map(({ gradeValue, term, result }) => {
              const x = getDataPointX(gradeValue, term, academicGrades);
              const tier = getTierFromResult(result);
              const originalTierIndex = getTierYIndex(tier);
              const { upperIndex, midIndex, lowerIndex } = getRangeIndices(originalTierIndex);

              return {
                x,
                upperY: yScale(upperIndex),
                midY: yScale(midIndex),
                lowerY: yScale(lowerIndex),
                upperIndex,
                midIndex,
                lowerIndex,
              };
            });

            const firstPoint = rangePoints[0];
            const shouldExtendToStart = firstPoint.x > PADDING_LEFT + X_INSET + 1;

            // Build polyline with optional extension to the left edge
            const buildLine = (getY: (p: (typeof rangePoints)[0]) => number) => {
              const points = rangePoints.map((p) => `${p.x},${getY(p)}`);

              if (shouldExtendToStart) points.unshift(`${PADDING_LEFT},${getY(firstPoint)}`);

              return points.join(' ');
            };

            const upperPolyline = buildLine((p) => p.upperY);
            const lowerPolyline = buildLine((p) => p.lowerY);

            // Fill polygon: upper left-to-right, then lower right-to-left
            const upperPoints = rangePoints.map((p) => `${p.x},${p.upperY}`);
            const lowerPoints = [...rangePoints].reverse().map((p) => `${p.x},${p.lowerY}`);

            if (shouldExtendToStart) {
              upperPoints.unshift(`${PADDING_LEFT},${firstPoint.upperY}`);
              lowerPoints.push(`${PADDING_LEFT},${firstPoint.lowerY}`);
            }

            const fillPoints = [...upperPoints, ...lowerPoints].join(' ');

            const lastPoint = assessmentDataPoints.at(-1);
            const mostRecentStatus = lastPoint
              ? getEffectiveStatus(lastPoint.result, minimumTrack, currentStatus)
              : null;

            return (
              <g>
                {/* Status-colored shaded range area */}
                <polygon
                  points={fillPoints}
                  fill={COLOR_TOKENS[getStatusColor(mostRecentStatus)]}
                  fillOpacity={0.15}
                />

                {/* Upper gray dotted range line */}
                <polyline
                  points={upperPolyline}
                  fill="none"
                  stroke={COLOR_TOKENS['Gray/400']}
                  strokeWidth={3}
                  strokeDasharray="6 6"
                />

                {/* Lower gray dotted range line */}
                <polyline
                  points={lowerPolyline}
                  fill="none"
                  stroke={COLOR_TOKENS['Gray/400']}
                  strokeWidth={3}
                  strokeDasharray="6 6"
                />

                {/* Black connecting line at top of band */}
                {rangePoints.length > 0 && (
                  <polyline
                    points={upperPolyline}
                    fill="none"
                    stroke={COLOR_TOKENS['Colors/Base/Black']}
                    strokeWidth={3}
                  />
                )}
              </g>
            );
          })()}

        {/* Extrapolation projection from most recent assessment (no fill) */}
        {assessmentDataPoints.length > 0 &&
          (() => {
            const mostRecent = assessmentDataPoints.at(-1);

            if (!mostRecent) return null;

            const { gradeValue, term, result: mostRecentResult } = mostRecent;
            const startX = getDataPointX(gradeValue, term, academicGrades);
            const tier = getTierFromResult(mostRecentResult);
            const originalTierIndex = getTierYIndex(tier);
            const { upperIndex, lowerIndex } = getRangeIndices(originalTierIndex);

            const endX = CHART_WIDTH;
            const xRange = endX - startX;

            if (xRange <= 0) return null;

            const mostRecentGradeIndex = academicGrades.findIndex((g) => g.value === gradeValue);
            const yearsRemaining = Math.max(0, gradeCount - 1 - mostRecentGradeIndex);

            if (yearsRemaining <= 0) return null;

            const pixelsPerYear = xRange / yearsRemaining;

            // Upper line: slopes up at fixed rate, flattens at top (index 0)
            const topClamp = 0;
            const yearsToTop =
              upperIndex <= topClamp ? 0 : (upperIndex - topClamp) / EXTRAPOLATION_POSITIVE_SLOPE;
            const upperHitsClamp = yearsToTop < yearsRemaining;

            const upperPoints = upperHitsClamp
              ? `${startX},${yScale(upperIndex)} ${startX + yearsToTop * pixelsPerYear},${yScale(topClamp)} ${endX},${yScale(topClamp)}`
              : `${startX},${yScale(upperIndex)} ${endX},${yScale(upperIndex - EXTRAPOLATION_POSITIVE_SLOPE * yearsRemaining)}`;

            // Lower line: slopes down at fixed rate, flattens at bottom
            const bottomClamp = TRACK_ORDER.length - 0.5;
            const yearsToBottom =
              lowerIndex >= bottomClamp
                ? 0
                : (bottomClamp - lowerIndex) / EXTRAPOLATION_NEGATIVE_SLOPE;
            const lowerHitsClamp = yearsToBottom < yearsRemaining;

            const lowerPoints = lowerHitsClamp
              ? `${startX},${yScale(lowerIndex)} ${startX + yearsToBottom * pixelsPerYear},${yScale(bottomClamp)} ${endX},${yScale(bottomClamp)}`
              : `${startX},${yScale(lowerIndex)} ${endX},${yScale(lowerIndex + EXTRAPOLATION_NEGATIVE_SLOPE * yearsRemaining)}`;

            return (
              <g>
                <polyline
                  points={upperPoints}
                  fill="none"
                  stroke={COLOR_TOKENS['Gray/400']}
                  strokeWidth={3}
                  strokeDasharray="6 6"
                />
                <polyline
                  points={lowerPoints}
                  fill="none"
                  stroke={COLOR_TOKENS['Gray/400']}
                  strokeWidth={3}
                  strokeDasharray="6 6"
                />
              </g>
            );
          })()}

        {/* Assessment data points */}
        {assessmentDataPoints.map(({ gradeValue, term, result }, index) => {
          const status = getEffectiveStatus(result, minimumTrack, currentStatus);
          const tier = getTierFromResult(result);
          const originalTierIndex = getTierYIndex(tier);
          const { upperIndex } = getRangeIndices(originalTierIndex);
          const x = getDataPointX(gradeValue, term, academicGrades);
          const y = yScale(upperIndex);
          const key = `${gradeValue}-${term}`;
          const isMostRecent = index === assessmentDataPoints.length - 1;

          const termLabel = getDataPointLabel(gradeValue, term);

          if (isMostRecent) {
            return (
              <foreignObject
                key={key}
                x={x - PROFILE_OUTER_SIZE / 2}
                y={y - PROFILE_OUTER_SIZE / 2}
                width={PROFILE_OUTER_SIZE}
                height={PROFILE_OUTER_SIZE}
                style={{ overflow: 'visible' }}
              >
                <Tooltip title={termLabel} arrow>
                  {profileImageUrl ? (
                    <Avatar src={profileImageUrl} sx={profileAvatarSx(status)} />
                  ) : (
                    <Box sx={profileAvatarFallbackSx(status)}>
                      <AppIcon
                        iconName="User01"
                        diameter="20px"
                        color="Colors/Foreground/fg-quaternary"
                      />
                    </Box>
                  )}
                </Tooltip>
              </foreignObject>
            );
          }

          return (
            <foreignObject
              key={key}
              x={x - SMALL_CIRCLE_RADIUS}
              y={y - SMALL_CIRCLE_RADIUS}
              width={SMALL_CIRCLE_RADIUS * 2}
              height={SMALL_CIRCLE_RADIUS * 2}
              style={{ overflow: 'visible' }}
            >
              <Tooltip title={termLabel} arrow>
                <Box
                  sx={{
                    width: SMALL_CIRCLE_RADIUS * 2,
                    height: SMALL_CIRCLE_RADIUS * 2,
                    borderRadius: '50%',
                    backgroundColor: COLOR_TOKENS[getStatusColor(status)],
                    border: `1px solid ${COLOR_TOKENS['Colors/Base/Black']}`,
                  }}
                />
              </Tooltip>
            </foreignObject>
          );
        })}

        {/* X-axis labels — one per academic grade + "Early Career" visual label on the right */}
        {academicGrades.map((grade, gradeIndex) => {
          const x = getGradeXPosition(gradeIndex, gradeCount);

          return (
            <foreignObject
              key={grade.value}
              x={x - 40}
              y={CHART_HEIGHT - PADDING_BOTTOM + LABEL_AREA_TOP}
              width={80}
              height={LABEL_AREA_HEIGHT}
            >
              <Typography variant="text-sm-regular" sx={xAxisLabelSx}>
                {GRADE_DISPLAY_LABELS[grade.value] ?? grade.name}
              </Typography>
            </foreignObject>
          );
        })}
        <foreignObject
          key="early-career"
          x={getColumnXPosition(gradeCount, gradeCount + EXTRA_LABEL_COLUMNS) - 40}
          y={CHART_HEIGHT - PADDING_BOTTOM + LABEL_AREA_TOP}
          width={80}
          height={LABEL_AREA_HEIGHT}
        >
          <Typography variant="text-sm-regular" sx={xAxisLabelSx}>
            {TIMELINE_CHART_STRINGS.gradeLabels.earlyCareer.early}
            <br />
            {TIMELINE_CHART_STRINGS.gradeLabels.earlyCareer.career}
          </Typography>
        </foreignObject>
      </svg>
    </Box>
  );
};

const chartContainerSx: SxProps<Theme> = {
  position: 'relative',
  width: '100%',
  aspectRatio: `${CHART_WIDTH} / ${CHART_HEIGHT}`,
  flex: 1,
  minHeight: 0,
};

const yAxisLabelSx: SxProps<Theme> = {
  color: COLOR_TOKENS['Gray/600'],
  textAlign: 'left',
  lineHeight: '20px',
  typography: 'text-lg-regular',
  '@container (min-width: 600px)': {
    typography: 'text-sm-regular',
  },
};

const xAxisLabelSx: SxProps<Theme> = {
  color: COLOR_TOKENS['Gray/600'],
  textAlign: 'center',
  lineHeight: '24px',
  typography: 'text-lg-regular',
  '@container (min-width: 600px)': {
    typography: 'text-sm-regular',
  },
};

const profileAvatarSx =
  (status: UnlockStatus | null): SxProps<Theme> =>
  ({ backrs }) => ({
    width: PROFILE_OUTER_SIZE,
    height: PROFILE_OUTER_SIZE,
    border: `1px solid ${COLOR_TOKENS['Colors/Base/Black']}`,
    outline: `3px solid ${COLOR_TOKENS[getStatusColor(status)]}`,
    outlineOffset: '2px',
    backgroundColor: backrs.colors['Colors/Background/bg-tertiary'],
  });

const profileAvatarFallbackSx =
  (status: UnlockStatus | null): SxProps<Theme> =>
  ({ backrs }) => ({
    width: PROFILE_OUTER_SIZE,
    height: PROFILE_OUTER_SIZE,
    border: `1px solid ${COLOR_TOKENS['Colors/Base/Black']}`,
    outline: `3px solid ${COLOR_TOKENS[getStatusColor(status)]}`,
    outlineOffset: '2px',
    backgroundColor: backrs.colors['Colors/Background/bg-tertiary'],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
  });
